import { ArrowLeft, ArrowRight, MapPinCheck, UsersRound } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { BrandLogo } from '../../components/BrandLogo';
import { InlineSpinner } from '../../components/LoadingIndicators';
import { ThemeToggle } from '../../components/ThemeToggle';
import { apiRequest, getApiErrorMessage } from '../../lib/api';
import type { ConsentPolicyConfiguration } from '../../types/domain';
import { GoogleBaSignIn } from './GoogleBaSignIn';
import { SignupPath } from './SignupPath';
import type { SignupAccountType } from './SignupPath';

export function BaRegistrationPage() {
  const { registerBrandAmbassador } = useAuth();
  const navigate = useNavigate();
  const [accountType, setAccountType] = useState<SignupAccountType>('brand_ambassador');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('Lahore');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [policies, setPolicies] = useState<ConsentPolicyConfiguration | null>(null);
  const [policyError, setPolicyError] = useState<string | null>(null);
  const [consentError, setConsentError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    void apiRequest<ConsentPolicyConfiguration>('/auth/consents/config/')
      .then((configuration) => { if (active) setPolicies(configuration); })
      .catch((cause: unknown) => { if (active) setPolicyError(getApiErrorMessage(cause)); });
    return () => { active = false; };
  }, []);

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setConsentError(null);
    if (password !== confirmation) {
      setError('Passwords do not match.');
      return;
    }
    if (!policies) {
      setError('The current policies are unavailable. Refresh and try again.');
      return;
    }
    if (!termsAccepted || !privacyAccepted) {
      setConsentError('Accept both the Terms of Service and Privacy Notice to create your account.');
      return;
    }
    setSubmitting(true);
    const message = await registerBrandAmbassador({
      accountType,
      email: email.trim(),
      fullName: fullName.trim(),
      phone: phone.trim() || null,
      city: city.trim(),
      password,
      termsAccepted,
      privacyAccepted,
      marketingConsent,
      termsVersion: policies.terms.version,
      privacyVersion: policies.privacy.version,
      marketingVersion: policies.marketing.version,
    });
    if (!message) {
      void navigate(accountType === 'brand_ambassador' ? '/talent' : '/', { replace: true });
      return;
    }
    setError(message);
    setSubmitting(false);
  }

  return (
    <main className="login-page registration-page">
      <ThemeToggle className="auth-theme-toggle" />
      <section className="login-story">
        <BrandLogo className="brand-logo-auth" variant="lockup" />
        <div>
          <p className="eyebrow">Join Brand Field Crew</p>
          <h1>Your account.<br />Your team.<br /><span>Your next opportunity.</span></h1>
          <p className="story-copy">Join as a Brand Ambassador or create a login for your organisation. Organisation access is always granted by an administrator.</p>
        </div>
        <div className="story-points">
          <span><UsersRound size={18} /> One reusable talent profile</span>
          <span><MapPinCheck size={18} /> Verified campaign opportunities</span>
        </div>
      </section>

      <section className="login-panel registration-panel">
        <GoogleBaSignIn key={accountType} initialAccountType={accountType} />
        <form className="login-form registration-form" onSubmit={(event) => { void submit(event); }}>
          <Link className="auth-back-link" to="/login"><ArrowLeft size={15} /> Back to sign in</Link>
          <p className="eyebrow">BFC registration</p>
          <h2>Create your account</h2>
          <SignupPath value={accountType} onChange={setAccountType} disabled={submitting} />

          <div className="auth-form-grid">
            <label>Full name<input autoComplete="name" onChange={(event) => setFullName(event.target.value)} required value={fullName} /></label>
            {accountType === 'brand_ambassador' ? <label>City<input autoComplete="address-level2" onChange={(event) => setCity(event.target.value)} required value={city} /></label> : null}
            <label>Email address<input autoComplete="email" inputMode="email" onChange={(event) => setEmail(event.target.value)} required type="email" value={email} /></label>
            <label>Phone number<input autoComplete="tel" onChange={(event) => setPhone(event.target.value)} placeholder="Optional" type="tel" value={phone} /></label>
            <label>Password<input autoComplete="new-password" minLength={8} onChange={(event) => setPassword(event.target.value)} required type="password" value={password} /></label>
            <label>Confirm password<input autoComplete="new-password" minLength={8} onChange={(event) => setConfirmation(event.target.value)} required type="password" value={confirmation} /></label>
          </div>

          <fieldset className="consent-fieldset" disabled={!policies}>
            <legend>Before you continue</legend>
            <p className="consent-intro">Review the required account policies and choose whether you want optional updates.</p>
            {!policies ? <p className="muted consent-loading">Loading current policies…</p> : null}
            <label className="consent-option consent-option-required">
              <input checked={termsAccepted} onChange={(event) => { setTermsAccepted(event.target.checked); setConsentError(null); }} type="checkbox" />
              <span className="consent-option-copy">
                <span>I accept the current <a href={policies?.terms.url ?? '/legal/terms'} rel="noreferrer" target="_blank">Terms of Service</a>.</span>
                <small>Required{policies ? ` · Version ${policies.terms.version}` : ''}</small>
              </span>
            </label>
            <label className="consent-option consent-option-required">
              <input checked={privacyAccepted} onChange={(event) => { setPrivacyAccepted(event.target.checked); setConsentError(null); }} type="checkbox" />
              <span className="consent-option-copy">
                <span>I have read and accept the current <a href={policies?.privacy.url ?? '/legal/privacy'} rel="noreferrer" target="_blank">Privacy Notice</a>.</span>
                <small>Required{policies ? ` · Version ${policies.privacy.version}` : ''}</small>
              </span>
            </label>
            <label className="consent-option consent-option-optional">
              <input checked={marketingConsent} onChange={(event) => setMarketingConsent(event.target.checked)} type="checkbox" />
              <span className="consent-option-copy">
                <span>Send me optional BFC product news and campaign opportunities.</span>
                <small>Optional · You can change this preference later</small>
              </span>
            </label>
            {consentError ? <p className="form-error" role="alert">{consentError}</p> : null}
          </fieldset>

          {policyError ? <p className="form-error" role="alert">Current policies could not be loaded: {policyError}</p> : null}
          {error ? <p className="form-error" role="alert">{error}</p> : null}
          <button className="button primary full" disabled={submitting || !policies} type="submit">
            {submitting ? <InlineSpinner /> : null} {submitting ? 'Creating account…' : accountType === 'brand_ambassador' ? 'Create BA account' : 'Create organisation account'} {!submitting ? <ArrowRight size={17} /> : null}
          </button>
        </form>
      </section>
    </main>
  );
}
